import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, Trash2, Users as UsersIcon, Edit } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminArtists = () => {
  const [artists, setArtists] = useState([]);
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    specialty: '',
    experience: '',
    bio: ''
  });
  const [uploading, setUploading] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const fetchArtists = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/artists`);
      setArtists(response.data);
    } catch (error) {
      toast.error('Failed to load artists');
    }
  };

  useEffect(() => {
    fetchArtists();
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select an image');
      return;
    }

    setUploading(true);
    const data = new FormData();
    data.append('file', file);
    data.append('name', formData.name);
    data.append('specialty', formData.specialty);
    data.append('experience', formData.experience);
    data.append('bio', formData.bio);

    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${BACKEND_URL}/api/admin/artists`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Artist added successfully!');
      setFile(null);
      setFormData({ name: '', specialty: '', experience: '', bio: '' });
      fetchArtists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${BACKEND_URL}/api/admin/artists/${editingArtist.id}`,
        {
          name: editingArtist.name,
          specialty: editingArtist.specialty,
          experience: editingArtist.experience,
          bio: editingArtist.bio
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      toast.success('Artist updated successfully');
      setShowEditDialog(false);
      fetchArtists();
    } catch (error) {
      toast.error('Failed to update artist');
    }
  };

  const handleDelete = async (artistId) => {
    if (!window.confirm('Are you sure you want to remove this artist?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/artists/${artistId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Artist removed successfully');
      fetchArtists();
    } catch (error) {
      toast.error('Failed to remove artist');
    }
  };

  return (
    <div>
      <h2 className="text-3xl font-black text-white mb-8 uppercase">Artists Management</h2>

      {/* Upload Form */}
      <Card className="bg-zinc-900 border-zinc-800 mb-8">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Upload className="w-5 h-5 text-red-600" />
            Add New Artist
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white font-bold uppercase text-sm">Artist Photo *</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files[0])}
                className="bg-zinc-800 border-zinc-700 text-white"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-white font-bold uppercase text-sm">Name *</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Artist name"
                  className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white font-bold uppercase text-sm">Specialty *</Label>
                <Input
                  type="text"
                  value={formData.specialty}
                  onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                  placeholder="e.g., Tattoo Artist"
                  className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white font-bold uppercase text-sm">Experience *</Label>
                <Input
                  type="text"
                  value={formData.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  placeholder="e.g., 5 years"
                  className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white font-bold uppercase text-sm">Bio *</Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Artist bio and description..."
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 min-h-24"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={uploading}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase"
            >
              {uploading ? 'Adding...' : 'Add Artist'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Artists Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {artists.map((artist) => (
          <Card key={artist.id} className="bg-zinc-900 border-zinc-800 group">
            <CardContent className="p-0">
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={`${BACKEND_URL}${artist.image_url}`}
                  alt={artist.name}
                  className="w-full h-full object-contain bg-zinc-800"
                />
              </div>
              <div className="p-4">
                <h3 className="text-white font-bold text-xl mb-1">{artist.name}</h3>
                <p className="text-red-500 font-semibold text-sm mb-2">{artist.specialty}</p>
                <p className="text-gray-400 text-sm mb-2">{artist.experience} experience</p>
                <p className="text-gray-500 text-xs mb-4 line-clamp-2">{artist.bio}</p>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setEditingArtist(artist);
                      setShowEditDialog(true);
                    }}
                    size="sm"
                    className="flex-1 bg-zinc-700 hover:bg-zinc-600"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDelete(artist.id)}
                    variant="destructive"
                    size="sm"
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {artists.length === 0 && (
        <div className="text-center py-20">
          <UsersIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No artists added yet</p>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Edit Artist</DialogTitle>
          </DialogHeader>
          {editingArtist && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editingArtist.name}
                  onChange={(e) => setEditingArtist({ ...editingArtist, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label>Specialty</Label>
                <Input
                  value={editingArtist.specialty}
                  onChange={(e) => setEditingArtist({ ...editingArtist, specialty: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label>Experience</Label>
                <Input
                  value={editingArtist.experience}
                  onChange={(e) => setEditingArtist({ ...editingArtist, experience: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label>Bio</Label>
                <Textarea
                  value={editingArtist.bio}
                  onChange={(e) => setEditingArtist({ ...editingArtist, bio: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white min-h-24"
                />
              </div>
              <Button
                onClick={handleUpdate}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminArtists;
